<script lang="ts" module>
  /**
   * Inline 24×24 stroke paths, drawn on a shared grid so weights match.
   * Inline rather than a sprite sheet: there are few enough that a second
   * network request costs more than the bytes save.
   */
  export const PATHS = {
    check: 'M20 6 9 17l-5-5',
    x: 'M18 6 6 18M6 6l12 12',
    arrowRight: 'M5 12h14M13 6l6 6-6 6',
    download: 'M12 3v12M7 11l5 5 5-5M4 20h16',
    apple: 'M12 6c.6-2 2.2-3 4-3 .2 1.8-.6 3.3-1.7 4.2M16.8 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.8-3.5.8s-1.9-.8-3.1-.8C7.2 7.4 5.6 8.3 4.8 9.8c-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8s1.9.8 3.1.7c1.3 0 2.1-1.2 2.9-2.4a10 10 0 0 0 1.3-2.6c-.1 0-2.6-1-2.6-4.1Z',
    lock: 'M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5z',
    key: 'M15 4a5 5 0 1 0-4.6 7L9 12.4 7.6 11l-1.4 1.4L4.8 11 3 12.8V16h3.2l6-6A5 5 0 0 0 15 4Zm.5 3.5h.01',
    server: 'M4 4h16v6H4zM4 14h16v6H4zM7.5 7h.01M7.5 17h.01',
    cloud: 'M17.5 19a4.5 4.5 0 0 0 .3-9 6 6 0 0 0-11.6 1.6A3.7 3.7 0 0 0 7 19z',
    shield: 'M12 3 4 6.5V12c0 5 3.4 8.2 8 9.5 4.6-1.3 8-4.5 8-9.5V6.5z',
    zap: 'M13 2 4 14h7l-1 8 9-12h-7z',
    layers: 'm12 3 9 5-9 5-9-5zM3 13l9 5 9-5M3 17l9 5 9-5',
    terminal: 'm5 7 5 5-5 5M12 17h7',
    mail: 'M3 6h18v12H3zM3 6l9 7 9-7',
    book: 'M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3zM18 7h2v13H7',
    external: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
    chevronDown: 'm6 9 6 6 6-6',
    menu: 'M4 7h16M4 12h16M4 17h16',
    sun: 'M12 5V3M12 21v-2M5 12H3M21 12h-2M6.3 6.3 4.9 4.9M19.1 19.1l-1.4-1.4M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
    moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z',
    copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
    user: 'M4 21v-2a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v2M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
    laptop: 'M5 5h14v10H5zM2 19h20',
    refresh: 'M20 11a8 8 0 1 0-.6 4M20 5v6h-6',
    alert: 'M12 8v5M12 16.5h.01M10.3 3.6 2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z',
    info: 'M12 16v-5M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    globe: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z',
  } as const

  export type IconName = keyof typeof PATHS
</script>

<script lang="ts">
  interface Props {
    name: IconName
    size?: number
    /** Give a label only when the icon is the sole carrier of meaning. */
    label?: string
    stroke?: number
  }

  let { name, size = 18, label, stroke = 1.7 }: Props = $props()
</script>

<svg
  class="icon"
  width={size}
  height={size}
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width={stroke}
  stroke-linecap="round"
  stroke-linejoin="round"
  role={label ? 'img' : 'presentation'}
  aria-label={label}
  aria-hidden={label ? undefined : 'true'}
>
  <path d={PATHS[name]} />
</svg>

<style>
  .icon { flex: none; display: block; }
</style>
